import React, { useState, useCallback, useEffect } from "react";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel.jsx";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from '../../hooks/useThemeColor';
import swal from "sweetalert";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";

const staffField = (staff, key) =>
  staff?.[key] ?? staff?.[key.charAt(0).toUpperCase() + key.slice(1)] ?? "";

const CompanyStaff = ({ CompID, showAddForm: externalShowAddForm, setShowAddForm: externalSetShowAddForm }) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [internalShowAddForm, setInternalShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Use external control if provided, otherwise use internal state
  const showAddForm = externalShowAddForm !== undefined ? externalShowAddForm : internalShowAddForm;
  const setShowAddForm = externalSetShowAddForm || setInternalShowAddForm;
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    UserName: "",
    Password: "",
    ConfirmPassword: "",
  });
  const [staffData, setStaffData] = useState({
    UserName: "",
    FirstName: "",
    LastName: "",
    Email: "",
    PhoneNo: "",
    PassCode: "",
    UserRoles: null,
    Active: true,
    CompID: parseInt(CompID),
    Password: "",
    ConfirmPassword: "",
    Country: "",
    Address: "",
    City: "",
    PostalCode: "",
    Gender: "",
    DateOfBirth: null,
    RatePerHour: 0,
  });

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID: CompID,
        },
      });
      const roleData = response.data.data || response.data;

      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
    } catch (err) {
      console.error("Error fetching roles:", err);
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  // Handle country/city changes
  const handleCountryCityChange = useCallback((data) => {
    setStaffData((prev) => ({
      ...prev,
      ...data,
    }));
    if (data.Country !== undefined) {
      setSelectedCountry(data.Country);
    }
    if (data.City !== undefined) {
      setSelectedCity(data.City);
    }
  }, []);

  // Fetch company staff
  const fetchCompanyStaff = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching staff for CompID:", CompID);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID: CompID,
          SortBy: "UserName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "100",
          PageNumber: 1,
        },
      });
 
      const rawData = response.data.data ?? response.data.Data ?? response.data;
      const staffData = Array.isArray(rawData) ? rawData : [];

      if (Array.isArray(staffData)) {
        setStaffList(staffData);
        console.log("Staff list set:", staffData);
      } else {
        console.warn("No staff data found or unexpected format:", staffData);
        setStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      showErrorToast("Failed to fetch staff members");
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setStaffData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleAddStaff = async () => {
    const { UserName, FirstName, LastName, Email, PhoneNo, PassCode, Password, ConfirmPassword } = staffData;
    const roleId = selectedRole?.value ?? staffData.UserRoles;

    if (!UserName.trim()) {
      showErrorToast("Please provide a username");
      return;
    }

    if (!FirstName.trim()) {
      showErrorToast("Please provide a first name");
      return;
    }

    if (!LastName.trim()) {
      showErrorToast("Please provide a last name");
      return;
    }

    if (!Email || !validateEmail(Email)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (!PassCode.trim()) {
      showErrorToast("Please provide a pass code");
      return;
    }

    if (!Password || Password !== ConfirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }

    if (!roleId) {
      showErrorToast("Please select a role");
      return;
    }

    try {
      setLoading(true);
      const addPayload = {
        UserName: staffData.UserName,
        FirstName: staffData.FirstName,
        LastName: staffData.LastName,
        Email: staffData.Email,
        PhoneNo: staffData.PhoneNo,
        PassCode: staffData.PassCode,
        Active: staffData.Active,
        CompID: parseInt(CompID),
        Password: staffData.Password,
        ComRolePermission_ID: roleId,
        Country: staffData.Country || selectedCountry || "",
        Address: staffData.Address || "",
        City: staffData.City || selectedCity || "",
        PostalCode: staffData.PostalCode || "",
        Gender: staffData.Gender || "",
        DateOfBirth: staffData.DateOfBirth || null,
        RatePerHour: staffData.RatePerHour || 0,
      };

      const response = await axiosInstance.post(APIs.ADD_STAFF, addPayload);

      if (response.data.status) {
        showSuccessToast("Staff member added successfully");
        setShowAddForm(false);
        resetForm();
        fetchCompanyStaff();
      } else {
        showErrorToast(response.data.message || "Error adding staff member");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error adding staff member");
      console.error("Error adding staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStaff = async () => {
    const { UserName, FirstName, LastName, Email, PassCode } = staffData;
    const roleId = selectedRole?.value ?? staffData.UserRoles;

    if (!UserName.trim()) {
      showErrorToast("Please provide a username");
      return;
    }

    if (!FirstName.trim()) {
      showErrorToast("Please provide a first name");
      return;
    }

    if (!LastName.trim()) {
      showErrorToast("Please provide a last name");
      return;
    }

    if (!Email || !validateEmail(Email)) {
      showErrorToast("Please provide a valid email");
      return;
    }

    if (!PassCode.trim()) {
      showErrorToast("Please provide a pass code");
      return;
    }

    if (!roleId) {
      showErrorToast("Please select a role");
      return;
    }

    try {
      setLoading(true);
      const updatePayload = {
        UserName: staffData.UserName,
        FirstName: staffData.FirstName,
        LastName: staffData.LastName,
        Email: staffData.Email,
        PhoneNo: staffData.PhoneNo,
        PassCode: staffData.PassCode,
        Active: staffData.Active,
        CompID: parseInt(CompID),
        Country: staffData.Country || selectedCountry || "",
        Address: staffData.Address || "",
        City: staffData.City || selectedCity || "",
        PostalCode: staffData.PostalCode || "",
        Gender: staffData.Gender || "",
        DateOfBirth: staffData.DateOfBirth || null,
        RatePerHour: staffData.RatePerHour || 0,
        Password: "",
        ComRolePermission_ID: roleId,
      };

      const response = await axiosInstance.put(APIs.UPDATE_STAFF, updatePayload);

      if (response.data.status) {
        showSuccessToast("Staff member updated successfully");
        setEditingStaff(null);
        resetForm();
        fetchCompanyStaff();
      } else {
        showErrorToast(response.data.message || "Error updating staff member");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error updating staff member");
      console.error("Error updating staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staff) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${staff.firstName} ${staff.lastName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_STAFF, {
        params: { 
          CompID: CompID,
          UserName: staff.userName 
        },
      });

      if (response.data.status) {
        showSuccessToast("Staff member deleted successfully");
        fetchCompanyStaff();
      } else {
        showErrorToast(response.data.message || "Error deleting staff member");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error deleting staff member");
      console.error("Error deleting staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const openPasswordModal = (staff) => {
    setPasswordForm({
      UserName: staff.userName || "",
      Password: "",
      ConfirmPassword: "",
    });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      UserName: "",
      Password: "",
      ConfirmPassword: "",
    });
  };

  const handleUpdatePassword = async () => {
    if (!passwordForm.UserName) {
      showErrorToast("Invalid username");
      return;
    }

    if (!passwordForm.Password) {
      showErrorToast("Please enter new password");
      return;
    }

    if (passwordForm.Password !== passwordForm.ConfirmPassword) {
      showErrorToast("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        APIs.UPDATE_PASSWORD,
        {
          UserName: passwordForm.UserName,
          Password: passwordForm.Password,
          ConfirmPassword: passwordForm.ConfirmPassword,
        },
        {
          params: { CompID: parseInt(CompID) },
        }
      );

      if (response.data.status) {
        showSuccessToast("Password updated successfully");
        closePasswordModal();
      } else {
        showErrorToast(response.data.message || "Failed to update password");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleEditStaff = (staff) => {
    setEditingStaff(staff);

    const roleId = staffField(staff, "comRolePermission_ID");
    const staffRole = roles.find((r) => r.value === roleId);
    setSelectedRole(staffRole || null);

    const country = staffField(staff, "country");
    const city = staffField(staff, "city");

    setSelectedCountry(country);
    setSelectedCity(city);

    setStaffData({
      UserName: staffField(staff, "userName"),
      FirstName: staffField(staff, "firstName"),
      LastName: staffField(staff, "lastName"),
      Email: staffField(staff, "email"),
      PhoneNo: staffField(staff, "phoneNo"),
      PassCode: staffField(staff, "passCode"),
      UserRoles: roleId || null,
      Active: staffField(staff, "active") !== false,
      CompID: parseInt(CompID),
      Password: "",
      ConfirmPassword: "",
      Country: country,
      Address: staffField(staff, "address"),
      City: city,
      PostalCode: staffField(staff, "postalCode"),
      Gender: staffField(staff, "gender"),
      DateOfBirth: staffField(staff, "dateOfBirth") || null,
      RatePerHour: staffField(staff, "ratePerHour") || 0,
    });
    setShowAddForm(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to get role name by ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.value === roleId);
    return role ? role.label : 'N/A';
  };

  // Handle status toggle
  const handleStatusToggle = async (staff) => {
    const newStatus = !staff.active;
    
    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.UPDATE_STAFF, {
        UserName: staff.userName,
        FirstName: staff.firstName,
        LastName: staff.lastName,
        Email: staff.email,
        PhoneNo: staff.phoneNo,
        PassCode: staff.passCode || "",
        ComRolePermission_ID: staff.comRolePermission_ID,
        Active: newStatus,
        CompID: parseInt(CompID),
        Country: staff.country || "",
        Address: staff.address || "",
        City: staff.city || "",
        PostalCode: staff.postalCode || "",
        Gender: staff.gender || "",
        DateOfBirth: staff.dateOfBirth || null,
        RatePerHour: staff.ratePerHour || 0,
      });

      if (response.data.status) {
        showSuccessToast(`Staff ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchCompanyStaff();
      } else {
        showErrorToast(response.data.message || "Failed to update staff status");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error updating staff status");
      console.error("Error updating staff status:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStaffData({
      UserName: "",
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNo: "",
      PassCode: "",
      UserRoles: null,
      Active: true,
      CompID: parseInt(CompID),
      Password: "",
      ConfirmPassword: "",
      Country: "",
      Address: "",
      City: "",
      PostalCode: "",
      Gender: "",
      DateOfBirth: null,
      RatePerHour: 0,
    });
    setSelectedRole(null);
    setSelectedCountry("");
    setSelectedCity("");
    setEditingStaff(null);
    setShowAddForm(false);
  };

  useEffect(() => {
    fetchRoles();
    fetchCompanyStaff();
  }, [fetchRoles, fetchCompanyStaff]);

  return (
    <div>
      {loading && <Preloader />}
 

      {/* Staff List */}
      <Panel>
        <PanelHeader className="bg-dark text-white">Staff Members</PanelHeader>
        <PanelBody>
          {staffList.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">No staff members found for this company.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped">
                <thead
                  style={{
                    backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                    color: isDarkMode ? "#ffffff" : "#000000",
                  }}
                >
                  <tr>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Username</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Full Name</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Email</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Phone</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Pass Code</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Role</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Status</th>
                    <th style={{ 
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                      borderBottom: isDarkMode ? "2px solid #404040" : "2px solid #dee2e6"
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((staff, index) => (
                    <tr key={index}>
                      <td>{staff.userName}</td>
                      <td>{staff.firstName} {staff.lastName}</td>
                      <td>{staff.email}</td>
                      <td>{staff.phoneNo || staff.phone}</td>
                      <td>{staff.passCode || 'N/A'}</td>
                      <td>{getRoleName(staff.comRolePermission_ID)}</td>
                      <td>
                        <ToggleSwitch
                          isChecked={staff.active !== false}
                          onToggle={() => handleStatusToggle(staff)}
                          label=""
                          name={`staff-status-${staff.userName}`}
                        />
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary me-2"
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            border: `1px solid ${themeColor}`,
                            color: themeColor,
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                          onClick={() => handleEditStaff(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = themeColor;
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = themeColor;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-warning me-2"
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            border: '1px solid #f0ad4e',
                            color: '#f0ad4e',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                          onClick={() => openPasswordModal(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f0ad4e';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#f0ad4e';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          title="Update Password"
                        >
                          <i className="fa fa-key" style={{ fontSize: '12px' }}></i>
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          style={{
                            width: '32px',
                            height: '32px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '6px',
                            border: '1px solid #dc3545',
                            color: '#dc3545',
                            transition: 'all 0.2s ease',
                            padding: 0
                          }}
                          onClick={() => handleDeleteStaff(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc3545';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#dc3545';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PanelBody>
      </Panel>

      {/* Add/Edit Staff Form Modal */}
      {showAddForm && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingStaff ? "Edit Staff Member" : "Add New Staff Member"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={resetForm}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Username"
                      placeHolder="Enter Username"
                      type="text"
                      value={staffData.UserName}
                      onchange={(e) => handleInputChange(e, "UserName")}
                      disabled={editingStaff !== null}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Email"
                      placeHolder="Enter Email Address"
                      type="email"
                      value={staffData.Email}
                      onchange={(e) => handleInputChange(e, "Email")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="First Name"
                      placeHolder="Enter First Name"
                      type="text"
                      value={staffData.FirstName}
                      onchange={(e) => handleInputChange(e, "FirstName")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Last Name"
                      placeHolder="Enter Last Name"
                      type="text"
                      value={staffData.LastName}
                      onchange={(e) => handleInputChange(e, "LastName")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Phone Number"
                      placeHolder="Enter Phone Number"
                      type="tel"
                      value={staffData.PhoneNo}
                      onchange={(e) => handleInputChange(e, "PhoneNo")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Pass Code *"
                      placeHolder="Enter Pass Code"
                      type="text"
                      value={staffData.PassCode}
                      onchange={(e) => handleInputChange(e, "PassCode")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label>Role</label>
                    <Select
                      value={selectedRole}
                      onChange={(option) => {
                        setSelectedRole(option);
                        setStaffData({
                          ...staffData,
                          UserRoles: option ? option.value : null,
                        });
                      }}
                      options={roles}
                      styles={dropdownStyles}
                      placeholder="Select Role"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll={false}
                      menuShouldScrollIntoView={false}
                      closeMenuOnScroll={false}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <CountryCitySelector
                    selectedCountry={selectedCountry}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChange={handleCountryCityChange}
                    setLoading={setLoading}
                    selectedValueIsLabel={true}
                    countryFieldName="Country"
                    cityFieldName="City"
                    isDarkMode={isDarkMode}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Address"
                      placeHolder="Enter Address"
                      type="text"
                      value={staffData.Address}
                      onchange={(e) => handleInputChange(e, "Address")}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Rate Per Hour"
                      placeHolder="Enter Rate Per Hour"
                      type="number"
                      value={staffData.RatePerHour}
                      onchange={(e) => handleInputChange(e, "RatePerHour")}
                      step="0.01"
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <ToggleSwitch
                      isChecked={staffData.Active}
                      onToggle={(e) => handleInputChange(e, "Active")}
                      label="Active Status"
                      name="Active"
                    />
                  </div>
                </div>

                {!editingStaff && (
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormInput
                        label="Password"
                        placeHolder="Enter Password"
                        type="password"
                        value={staffData.Password}
                        onchange={(e) => handleInputChange(e, "Password")}
                      />
                    </div>
                    <div className="col-6">
                      <FormInput
                        label="Confirm Password"
                        placeHolder="Confirm Password"
                        type="password"
                        value={staffData.ConfirmPassword}
                        onchange={(e) => handleInputChange(e, "ConfirmPassword")}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={editingStaff ? handleUpdateStaff : handleAddStaff}
                  disabled={loading}
                >
                  {loading ? "Processing..." : editingStaff ? "Update" : "Add"} Staff
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPasswordModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Password ({passwordForm.UserName})</h5>
                <button type="button" className="btn-close" onClick={closePasswordModal}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-12">
                    <FormInput
                      label="New Password"
                      placeHolder="Enter New Password"
                      type="password"
                      value={passwordForm.Password}
                      onchange={(e) => setPasswordForm((prev) => ({ ...prev, Password: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="row mb-1">
                  <div className="col-12">
                    <FormInput
                      label="Confirm New Password"
                      placeHolder="Confirm New Password"
                      type="password"
                      value={passwordForm.ConfirmPassword}
                      onchange={(e) => setPasswordForm((prev) => ({ ...prev, ConfirmPassword: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closePasswordModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleUpdatePassword} disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyStaff;

